// SoftphoneError.ts

import SoftphoneService from "./softphoneService";

export class SoftphoneError extends Error {
  type: string;
  code: number;
  userMessage: string;

  constructor(type: string, code: number, message: string) {
    const fullMessage = `[${type}]: ${message}`;
    super(fullMessage);
    this.type = type;
    this.code = code;
    this.userMessage = fullMessage;
  }
}

// Define error types for categorization
export const ErrorTypes = {
  ConfigurationError: "ConfigurationError",
  RegistrationError: "RegistrationError",
  CallError: "CallError",
  ConnectionError: "ConnectionError",
  WebSocketError: "WebSocketError",
  MediaError: "MediaError",
  SDPError: "SDPError",
  ICEError: "ICEError",
  // Add more types as needed...
};

// Define specific error codes and messages
export const ErrorCodes = {
  // Configuration Errors
  MissingConfiguration: {
    code: 1001,
    message: "Configuration for SIP is incomplete or missing.",
  },
  InvalidPort: {
    code: 1002,
    message: "The specified port for SIP server is invalid.",
  },
  // Registration  Errors
  ConnectionTimeout: {
    code: 2001,
    message: "Connection attempt to the SIP server timed out.",
  },
  ConnectionFailed: {
    code: 2002,
    message: "Could not connect to the SIP server.",
  },

  PeerConnectionError: {
    code: 2003,
    message: "No peer connection established.",
  },

  needServerConnection: {
    code: 200,
    message: "Please connect to the server first.",
  },

  // SDP Errors
  SDPGenerationFailed: {
    code: 2004,
    message: "Failed to generate SDP for the call.",
  },
  SDPProcessingFailed: {
    code: 2005,
    message: "Failed to process the SDP for the call.",
  },
  // SDP exchange errors
  SDPExchangeFailed: {
    code: 2006,
    message: "Failed to exchange SDP for the call.",
  },

  // ICE candidate errors
  ICECandidateFailed: {
    code: 2007,
    message: "Failed to gather ICE candidates for the call.",
  },
  ICEConnectionFailed: {
    code: 2008,
    message: "Failed to establish ICE connection for the call.",
  },

  // Registration Errors
  CredentialsInvalid: {
    code: 3001,
    message: "The provided SIP credentials are invalid.",
  },
  RegistrationFailed: {
    code: 3002,
    message: "Failed to register the SIP user agent.",
  },
  // Call Errors
  CallFailed: {
    code: 4001,
    message: "The call could not be completed.",
  },
  CallEnded: {
    code: 4002,
    message: "The call ended unexpectedly.",
  },

  CallNotEnded: {
    code: 4006,
    message: "The call could not be ended.",
  },
  NumberInvalid: {
    code: 4003,
    message: "The dialed number is invalid.",
  },
  PhoneNumberNotFound: {
    code: 4009,
    message: "No phone number found for this tracker.",
  },
  UserAgentNotInitialized: {
    code: 4004,
    message: "The SIP user agent has not been initialized.",
  },

  UserAgentUnavailable: {
    code: 4008,
    message: "The SIP user agent is not available.",
  },

  NoSessionToAnswer: {
    code: 4007,
    message: "There is no incoming call session to answer.",
  },

  SessionCreationFailed: {
    code: 4005,
    message: "Failed to create a new call session.",
  },

  UnexpectedError: {
    code: 4099,
    message: "An unexpected error occurred.",
  },

  NoCurrentSession: {
    code: 4100,
    message: "There is no current call session to end.",
  },
  // call rejected by remote user
  CallRejected: {
    code: 4101,
    message: `The call was rejected by the remote user.`,
  },

  // WebSocket Errors
  WebSocketConnectionFailed: {
    code: 5001,
    message: "WebSocket connection to the SIP server failed.",
  },
  // Network Errors
  NetworkUnreachable: {
    code: 6001,
    message: "Network is unreachable. Check your internet connection.",
  },
  NetworkTimeout: {
    code: 6002,
    message: "Network request timed out. Please try again.",
  },

  // Media Errors
  MicrophoneAccessDenied: {
    code: 7001,
    message: "Microphone access was denied. Please allow access to make calls.",
  },
  CameraAccessDenied: {
    code: 7002,
    message: "Camera access was denied. Please allow access for video calls.",
  },
  MediaDeviceNotFound: {
    code: 7003,
    message: "The specified media device was not found.",
  },
  LocalMediaStreamNotPresent: {
    code: 7004,
    message: "Media stream is not present.",
  },

  // SIP Protocol Errors
  SIPBadRequest: {
    code: 8001,
    message: "The SIP request is malformed or incomplete.",
  },
  SIPUnauthorized: {
    code: 8002,
    message: "Authentication failed with the SIP server.",
  },
  SIPForbidden: {
    code: 8003,
    message:
      "The server is refusing the action. Check permissions or account status.",
  },
  SIPNotFound: {
    code: 8004,
    message: "The called party is not found on the server.",
  },
  SIPMethodNotAllowed: {
    code: 8005,
    message: "The method used in the request is not allowed.",
  },
  SIPTemporarilyUnavailable: {
    code: 8006,
    message: "The called party is temporarily unavailable.",
  },
  SIPTransactionFailed: {
    code: 8007,
    message: "The SIP transaction failed.",
  },
  // Add more codes and messages as needed...
};
